import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { api } from '@core/api';
import { Checkbox } from '@shared/components/ui/checkbox';
import { PhoneInput } from '@shared/components/common/PhoneInput';
import { isValidPhone } from '@shared/utils/phoneValidation';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useUserProfile } from '../hooks/useUserProfile';

const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;

/**
 * Shared input styling — matches the cardholder-name input on
 * StripePaymentForm so profile forms read on-system with the rest of
 * the booking flow. Drops the legacy `rounded-full` pill input pattern
 * (anti-pattern in current design system; pills are reserved for the
 * primary CTA, not field surfaces).
 */
const INPUT_CLASS =
  'w-full border border-border rounded-xl px-4 py-3 text-base bg-card text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const LABEL_CLASS = 'block text-sm font-medium text-foreground/70 mb-1.5';

const SECTION_HEADING_CLASS = 'text-2xl font-display mb-5';

const PRIMARY_BUTTON_CLASS =
  'w-full py-3 rounded-xl bg-brand text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card';

const MyProfileTab = () => {
  const { t } = useTranslation('profile');
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const restaurant = useRestaurant();

  // Profile form state (current values shown in inputs)
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Baseline — initial values from server / last successful save. We
  // compare current against baseline to compute dirtiness; after a
  // successful save we advance the baseline so the form is "pristine"
  // again until the user makes new edits. This drives the Save button's
  // enabled state — SOTA settings-form pattern (Linear, Stripe Dashboard,
  // Vercel, GitHub settings, Notion). No edit/save toggle, no accidental
  // no-op submits, button-enabled IS the dirty indicator.
  //
  // Marketing-consent baseline NOT tracked — see the dirty-state comment
  // below; deliberate so the button doesn't enable on a toggle whose
  // value can't currently be persisted server-side.
  const [baselineName, setBaselineName] = useState('');
  const [baselinePhone, setBaselinePhone] = useState('');

  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState('');

  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Seed from server data ONCE on first arrival. Subsequent profile
  // refetches (window focus, query invalidation, etc.) must NOT clobber
  // in-progress user edits — the user typing into the name field would
  // see their input reset mid-keystroke, which is a real bug if the
  // useUserProfile query refetches.
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (hasSeededRef.current) return;
    if (!profile) return;
    hasSeededRef.current = true;
    setName(profile.name);
    setBaselineName(profile.name);
    setPhone(profile.phone);
    setBaselinePhone(profile.phone);
    setMarketingConsent(profile.optInEmail);
    // marketingConsent baseline intentionally not tracked — see
    // dirty-state comment below.
  }, [profile]);

  // Dirty state — only fields the API actually persists (name + phone).
  // Marketing consent is deliberately excluded from both the dirty
  // check and the API payload because the BE endpoint hasn't been
  // confirmed to accept the field. Including it risks a silent BE
  // drop → button stays enabled forever / UI lies about persistence.
  // Once BE confirms, add `marketingConsent !== baselineMarketing` back
  // here and include `optInEmail` in the payload.
  //
  // Trimmed compare avoids the "typed a trailing space → button stays
  // enabled" pathology (baselines are stored trimmed after each save).
  const isProfileDirty =
    name.trim() !== baselineName ||
    phone.trim() !== baselinePhone;

  // Save is enabled only when the form is dirty AND values are
  // shape-valid (name not empty, phone valid if provided). Deep
  // validation (PHONE format etc.) runs on submit and surfaces as an
  // error — but if the user is editing something obviously invalid, we
  // don't even let them try.
  const canSaveProfile =
    isProfileDirty && !!name.trim() && (!phone || isValidPhone(phone));

  // Password change is enabled when both fields are populated. Regex
  // validation runs on submit (so we can surface specific errors like
  // "minimum 8 chars" rather than just refusing to enable the button).
  const canChangePassword = !!newPassword && !!confirmPassword;

  const handleUpdateProfile = async () => {
    setUpdateError('');
    setUpdateSuccess('');

    if (!name.trim()) {
      setUpdateError(t('MY_ACCOUNT_VALIDATION_NAME_REQUIRED'));
      return;
    }

    if (phone && !isValidPhone(phone)) {
      setUpdateError(t('MY_ACCOUNT_VALIDATION_PHONE_REQUIRED'));
      return;
    }

    setUpdateLoading(true);
    try {
      // NOTE: `optInEmail` (marketing consent) deliberately NOT sent.
      // The other caller of /api/update-user-info (CompleteProfilePage)
      // doesn't send it, and the BE acceptance is unconfirmed. Sending
      // it now risks a silent BE drop → the UI shows "saved" while the
      // server doesn't persist the toggle. Filed as followup: confirm
      // BE controller accepts the field, then add to payload + advance
      // baselineMarketing on success. Until then the consent toggle is
      // local-state-only (matching pre-redesign behavior).
      await api.post('/api/update-user-info', {
        email: profile?.email,
        name: name.trim(),
        phone: phone.trim(),
      });
      setUpdateSuccess(t('MY_ACCOUNT_MESSAGE_1'));
      // Advance baseline for fields we actually persisted. Marketing
      // consent baseline NOT advanced — see comment above.
      setName(name.trim());
      setPhone(phone.trim());
      setBaselineName(name.trim());
      setBaselinePhone(phone.trim());
    } catch {
      setUpdateError(t('MY_ACCOUNT_MESSAGE_UPDATE_ERROR'));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!newPassword) {
      setPasswordError(t('MY_ACCOUNT_VALIDATION_PASSWORD_REQUIRED'));
      return;
    }

    if (!PASSWORD_REGEX.test(newPassword)) {
      setPasswordError(t('MY_ACCOUNT_VALIDATION_PASSWORD_INVALID'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('MY_ACCOUNT_VALIDATION_PASSWORD_MISMATCH'));
      return;
    }

    setPasswordLoading(true);
    try {
      await api.post('/api/update-password', { newPassword });
      setPasswordSuccess(t('MY_ACCOUNT_MESSAGE_2'));
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setPasswordError(t('MY_ACCOUNT_MESSAGE_PASSWORD_ERROR'));
    } finally {
      setPasswordLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="w-full flex justify-center py-8">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      {/* Personal data */}
      <section>
        <h2 className={SECTION_HEADING_CLASS}>{t('MY_ACCOUNT_PERSONAL_DATA')}</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="profile-name" className={LABEL_CLASS}>
              {t('MY_ACCOUNT_LABEL_NAME')}
            </label>
            <input
              id="profile-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="profile-email" className={LABEL_CLASS}>
              {t('MY_ACCOUNT_LABEL_EMAIL')}
            </label>
            <input
              id="profile-email"
              type="email"
              value={profile?.email || ''}
              disabled
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="profile-phone" className={LABEL_CLASS}>
              {t('MY_ACCOUNT_LABEL_PHONE')}
            </label>
            <div className={INPUT_CLASS + ' flex items-center !py-2.5'}>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                defaultCountry={restaurant.isoCountryCode}
                dialCode={restaurant.countryCode}
              />
            </div>
          </div>

          {updateError && (
            <p className="text-destructive text-sm" role="alert">
              {updateError}
            </p>
          )}
          {updateSuccess && (
            <p className="text-brand text-sm" role="status">
              {updateSuccess}
            </p>
          )}

          <button
            type="button"
            className={PRIMARY_BUTTON_CLASS}
            onClick={handleUpdateProfile}
            disabled={!canSaveProfile || updateLoading}
          >
            {updateLoading ? (
              <Loader2 size={18} className="animate-spin mx-auto" aria-label={t('MY_ACCOUNT_LABEL_UPDATE_DATA')} />
            ) : (
              t('MY_ACCOUNT_LABEL_UPDATE_DATA')
            )}
          </button>
        </div>
      </section>

      {/* Divider — visual break between distinct sections so they don't
          read as one long form. */}
      <div className="border-t border-border my-8" />

      {/* Change password */}
      <section>
        <h2 className={SECTION_HEADING_CLASS}>{t('MY_ACCOUNT_CHANGE_PASSWORD')}</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="new-password" className={LABEL_CLASS}>
              {t('MY_ACCOUNT_LABEL_NEW_PASSWORD')}
            </label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="confirm-password" className={LABEL_CLASS}>
              {t('MY_ACCOUNT_LABEL_CONFIRM_PASSWORD')}
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className={INPUT_CLASS}
            />
          </div>

          {passwordError && (
            <p className="text-destructive text-sm" role="alert">
              {passwordError}
            </p>
          )}
          {passwordSuccess && (
            <p className="text-brand text-sm" role="status">
              {passwordSuccess}
            </p>
          )}

          <button
            type="button"
            className={PRIMARY_BUTTON_CLASS}
            onClick={handleChangePassword}
            disabled={!canChangePassword || passwordLoading}
          >
            {passwordLoading ? (
              <Loader2 size={18} className="animate-spin mx-auto" aria-label={t('MY_ACCOUNT_LABEL_UPDATE_PASSWORD')} />
            ) : (
              t('MY_ACCOUNT_LABEL_UPDATE_PASSWORD')
            )}
          </button>
        </div>
      </section>

      {/* Divider before secondary preferences */}
      <div className="border-t border-border my-8" />

      {/* Marketing consent */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="marketing-consent"
          checked={marketingConsent}
          onCheckedChange={(checked) => setMarketingConsent(checked === true)}
          className="mt-0.5"
        />
        <label
          htmlFor="marketing-consent"
          className="text-sm text-foreground/70 leading-snug cursor-pointer"
        >
          {t('PROFILE_MARKETING_CONSENT')}
        </label>
      </div>
    </div>
  );
};

export default MyProfileTab;
